import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { logError } from "@/lib/log";

const Recipient = ({ w, testid }) => {
  const [open, setOpen] = useState(false);
  const long = w.note && w.note.length > 80;
  return (
    <div className="pl-3 border-l-2 border-mm-primary/30" data-testid={testid}>
      <span className="font-medium">{w.name}</span>
      {w.watched && <span className="text-xs text-mm-success ml-2">Watched ✓</span>}
      {w.hearted && <span className="text-xs text-mm-primary ml-2" aria-label="Hearted">♥</span>}
      {w.note && (
        <button className="block text-left text-xs text-mm-muted italic mt-0.5 break-words" onClick={() => long && setOpen((o) => !o)} data-testid={`${testid}-note`}>
          “{open || !long ? w.note : `${w.note.slice(0, 80)}…`}”{long && <span className="not-italic text-mm-primary ml-1">{open ? "less" : "more"}</span>}
        </button>
      )}
    </div>
  );
};

const SharedVideos = ({ list, onRemove }) => (list.length ? (
  <div className="mt-3 space-y-1.5" data-testid="year-video-shared-list">
    <div className="text-xs font-semibold text-mm-muted">Shared videos (links work until you remove them)</div>
    {list.map((v) => (
      <div key={v.id} className="flex items-start justify-between gap-2 text-sm mm-soft p-2.5" data-testid={`year-video-shared-${v.id}`}>
        <div className="min-w-0 space-y-1.5">
          <div className="text-xs font-semibold">{v.label}</div>
          {v.shared_with.map((w, i) => <Recipient key={w.supporter_id || `${w.name}-${i}`} w={w} testid={`year-video-recipient-${v.id}-${i}`} />)}
        </div>
        <button className="p-1.5 rounded-full hover:bg-mm-soft text-mm-error" onClick={() => onRemove(v.id)} aria-label="Remove video" data-testid={`year-video-remove-${v.id}`}><Trash2 size={14} /></button>
      </div>
    ))}
  </div>
) : null);

export const YearVideoSend = ({ y, makeVideo, busy }) => {
  const [sups, setSups] = useState([]);
  const [ids, setIds] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [videos, setVideos] = useState([]);

  const loadVideos = () => api.get("/year-story/videos").then((r) => setVideos(r.data)).catch((e) => logError("Videos failed:", e));
  useEffect(() => {
    api.get("/supporters").then((r) => setSups(r.data.filter((s) => s.email))).catch((e) => logError("Supporters failed:", e));
    loadVideos();
  }, []);

  const pick = (id) => setIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : l.length >= 5 ? l : [...l, id]));
  const send = async () => {
    setSending(true);
    try {
      const blob = await makeVideo();
      const form = new FormData();
      form.append("file", new File([blob], "year.webm", { type: "video/webm" }));
      form.append("supporter_ids", ids.join(","));
      form.append("period", y.period);
      form.append("label", y.label);
      form.append("message", message);
      const { data } = await api.post("/year-story/video/share", form);
      toast.success(data.message);
      setIds([]); setMessage("");
    } catch (e) { toast.error(errMsg(e, "Could not send your video")); } finally { setSending(false); loadVideos(); }
  };
  const remove = async (id) => {
    try { await api.delete(`/year-story/videos/${id}`); toast.success("Video removed — its links no longer work"); loadVideos(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="border-t pt-3" data-testid="year-video-send">
      <div className="text-sm font-semibold">Send to supporters</div>
      {sups.length ? (
        <>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {sups.map((s) => <button key={s.id} className={`mm-chip !py-1.5 ${ids.includes(s.id) ? "mm-chip-on" : ""}`} onClick={() => pick(s.id)} data-testid={`year-video-supporter-${s.id}`}>{s.name}</button>)}
          </div>
          <Textarea placeholder="Add a short note (optional)" value={message} onChange={(e) => setMessage(e.target.value.slice(0, 300))} rows={2} className="mt-2" data-testid="year-video-message" />
          <button className="mm-btn w-full mt-2 inline-flex items-center justify-center gap-2" disabled={busy || sending || !ids.length} onClick={send} data-testid="year-video-send-button">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {sending ? "Making & sending… (about 20s)" : `Send private link${ids.length > 1 ? ` to ${ids.length}` : ""}`}
          </button>
        </>
      ) : <p className="text-xs text-mm-muted mt-1" data-testid="year-video-no-supporters">Add a supporter with an email to send your video.</p>}
      <SharedVideos list={videos} onRemove={remove} />
    </div>
  );
};
